import { useMemo, useState, type ReactNode } from "react"
import { CaretRight, FunnelSimple, Info } from "@phosphor-icons/react"
import { z } from "zod"

import { Badge, Checkbox, IconButton } from "~/components/design-system/components"
import { DisplayOptionsPopover } from "~/components/planning/DisplayOptionsPopover"
import ExpandableSearchField from "~/components/planning/ExpandableSearchField"
import FilterPopover from "~/components/planning/FilterPopover"
import { useFilters } from "~/hooks/useFilters"
import {
  FilterFieldsOverrideContext,
  type FilterFieldsOverride,
} from "~/hooks/useFilterFieldsOverride"
import { usePermission } from "~/hooks/usePermission"
import { useSafeLocalStorage } from "~/hooks/useSafeLocalStorage"
import { FilterScopeProvider } from "~/providers/FilterScopeProvider"
import type { FilterFieldType } from "~/utils/filter"
import { cn } from "~/utils/helpers"
import { filterRowsById, GroupByDisplayOption } from "./AllocationFilters"
import { matchesStoreSearch, STORE_SEARCH_OPTIONS_NAME_AND_ADDRESS } from "~/utils/storeSearch"
import {
  ASSIGNED_LABEL,
  assignStateOf,
  groupRowsBy,
  type AssignState,
} from "./storeAssignmentModel"
import { useStoreSearch } from "./useStoreSearch"
import { distinctValues } from "~/utils/tsUtils"

export type StoreAssignmentRow = {
  id: string
  storeName: string
  city: string
  checked: boolean
  changed?: boolean
}

export type StoreAssignmentColumn<T> = {
  field: keyof T & string
  name: string
  longName?: string
  type: "string" | "number"
  className: string
  render?: (row: T) => ReactNode
}

type ViewRow<T> = T & { assigned: string }

type StoreAssignmentOptions<T extends StoreAssignmentRow> = {
  scopeKey: string
  rows: T[]
  columns: StoreAssignmentColumn<T>[]
  onAssign: (storeIds: string[], checked: boolean) => void
  /** Locks the table and explains why, e.g. when the target is not active. */
  disabledReason?: string
  groupable?: boolean
}

const NO_GROUPING = "none"
const groupBySchema = z.string()

type GroupKey<T> = typeof NO_GROUPING | "assigned" | (keyof T & string)

function cellText(value: unknown) {
  if (Array.isArray(value)) return value.join(", ")
  return value == null ? "" : String(value)
}

/** One checkbox for a set of stores: ticks them all, or clears them all once every one is in. */
export function AssignSetCheckbox({
  ids,
  state,
  onAssign,
  disabledReason,
  hint,
  ariaLabel,
  onClick,
}: {
  ids: string[]
  state: AssignState
  onAssign: (storeIds: string[], checked: boolean) => void
  disabledReason: string | undefined
  hint?: string
  ariaLabel?: string
  onClick?: (event: React.MouseEvent) => void
}) {
  return (
    <Checkbox
      checked={state === "all"}
      indeterminate={state === "some"}
      onCheckedChange={() => onAssign(ids, state !== "all")}
      onClick={onClick}
      disabled={disabledReason !== undefined || ids.length === 0}
      requires="edit"
      allowedTitle={disabledReason ?? hint}
      aria-label={ariaLabel}
      className="size-3.5 rounded-xs"
      iconClassName="size-3"
    />
  )
}

/** State for a store assignment table; call it inside a `FilterScopeProvider` keyed by `scopeKey`. */
export function useStoreAssignment<T extends StoreAssignmentRow>({
  scopeKey,
  rows,
  columns,
  onAssign,
  disabledReason,
  groupable = false,
}: StoreAssignmentOptions<T>) {
  const { search, setSearch, searchBy, setSearchBy } = useStoreSearch(
    `store-assignment-${scopeKey}`,
    STORE_SEARCH_OPTIONS_NAME_AND_ADDRESS,
  )
  const { filters } = useFilters()
  const { allowed, reason } = usePermission("edit")
  const [storedGroupBy, setGroupBy] = useSafeLocalStorage(
    `${scopeKey}.group-by`,
    NO_GROUPING,
    groupBySchema,
  )
  const [openGroups, setOpenGroups] = useState<ReadonlySet<string>>(new Set())
  const narrowed = search !== "" || filters.length > 0

  const groupOptions = useMemo<{ key: GroupKey<T>; label: string }[]>(
    () => [
      { key: NO_GROUPING, label: "None" },
      { key: "assigned", label: "Assigned" },
      ...columns
        .filter((column) => column.type === "string")
        .map((column) => ({ key: column.field, label: column.longName ?? column.name })),
    ],
    [columns],
  )
  const groupBy: GroupKey<T> =
    (groupable && groupOptions.find((option) => option.key === storedGroupBy)?.key) || NO_GROUPING

  const filterFields = useMemo<FilterFieldType[]>(
    () => [
      { value: "storeName", name: "Store", type: "string" },
      { value: "assigned", name: "Assigned", type: "string" },
      ...columns.map((column) => ({
        value: column.field,
        name: column.longName ?? column.name,
        type: column.type,
      })),
    ],
    [columns],
  )
  const fieldsOverride = useMemo<FilterFieldsOverride>(() => {
    const optionsByField: Record<string, string[]> = {
      storeName: distinctValues(rows.map((row) => row.storeName)),
      assigned: [ASSIGNED_LABEL.true, ASSIGNED_LABEL.false],
    }
    for (const column of columns) {
      if (column.type !== "string") continue
      optionsByField[column.field] = distinctValues(
        rows.flatMap((row) => {
          const value = row[column.field]
          return Array.isArray(value) ? value : [cellText(value)]
        }),
      )
    }
    return { fields: filterFields, optionsByField }
  }, [rows, columns, filterFields])

  const visible = useMemo(() => {
    const searched: ViewRow<T>[] = rows
      .filter((row) =>
        matchesStoreSearch({ name: row.storeName, address: row.city }, search, searchBy),
      )
      .map((row) => ({ ...row, assigned: ASSIGNED_LABEL[`${row.checked}`] }))
    const fieldType = (field: string) =>
      filterFields.find((candidate) => candidate.value === field)?.type
    return filterRowsById(searched, filters, fieldType)
  }, [rows, filters, search, searchBy, filterFields])

  const groups = useMemo(
    () =>
      groupBy === NO_GROUPING
        ? undefined
        : groupRowsBy(visible, (row: ViewRow<T>) => cellText(row[groupBy])),
    [visible, groupBy],
  )

  const assign = (storeIds: string[], checked: boolean) => {
    if (groupBy === "assigned") {
      setOpenGroups((prev) => new Set(prev).add(ASSIGNED_LABEL[`${checked}`]))
    }
    onAssign(storeIds, checked)
  }

  return {
    search,
    setSearch,
    searchBy,
    setSearchBy,
    fieldsOverride,
    groupable,
    groupOptions,
    groupBy,
    setGroupBy,
    groups,
    openGroups,
    narrowed,
    toggleGroup: (key: string) =>
      setOpenGroups((prev) => {
        const next = new Set(prev)
        if (!next.delete(key)) next.add(key)
        return next
      }),
    visible,
    rowCount: rows.length,
    columns,
    onAssign: assign,
    disabledReason,
    lockReason: disabledReason ?? (allowed ? undefined : reason),
  }
}

export type StoreAssignmentView<T extends StoreAssignmentRow> = ReturnType<
  typeof useStoreAssignment<T>
>

export function StoreAssignmentToolbar<T extends StoreAssignmentRow>({
  view,
}: {
  view: StoreAssignmentView<T>
}) {
  return (
    <FilterFieldsOverrideContext.Provider value={view.fieldsOverride}>
      <ExpandableSearchField
        initialSearchText={view.search}
        onSearchTextChange={view.setSearch}
        placeholder="Search stores..."
        debounceTime={200}
        searchBy={view.searchBy}
        onSearchByChange={view.setSearchBy}
        searchByOptions={STORE_SEARCH_OPTIONS_NAME_AND_ADDRESS}
      />
      <FilterPopover
        align="end"
        editGroup
        customTrigger={
          <IconButton variant="neutral" size="sm" aria-label="Add filter">
            <FunnelSimple />
          </IconButton>
        }
      />
      {view.groupable && (
        <DisplayOptionsPopover
          extraOptions={
            <GroupByDisplayOption
              options={view.groupOptions}
              value={view.groupBy}
              onChange={view.setGroupBy}
            />
          }
        />
      )}
    </FilterFieldsOverrideContext.Provider>
  )
}

function emptyText(loading: boolean, rowCount: number) {
  if (loading) return "Loading stores…"
  if (rowCount === 0) return "No stores yet. Stores load with the retail location data."
  return "No stores match the search or filters."
}

const stopPropagation = (event: React.MouseEvent) => event.stopPropagation()

/** The store rows, optionally grouped. `bounded` caps the height so the cards below stay in reach. */
export function StoreAssignmentRows<T extends StoreAssignmentRow>({
  view,
  targetLabel,
  loading = false,
  bounded = false,
  fill = false,
  showDelta = false,
}: {
  view: StoreAssignmentView<T>
  targetLabel: string
  loading?: boolean
  bounded?: boolean
  fill?: boolean
  /** Badge changed rows as Added or Removed. */
  showDelta?: boolean
}) {
  const { visible, groups, columns, onAssign, disabledReason, lockReason } = view

  const storeRow = (row: ViewRow<T>, indented: boolean) => {
    const toggle = () => {
      if (lockReason === undefined) onAssign([row.id], !row.checked)
    }
    return (
      <div
        key={row.id}
        title={lockReason}
        className={cn(
          "flex items-center gap-2.5 border-b border-base px-3 py-1.5 last:border-b-0",
          indented && "pl-9",
          lockReason === undefined && "cursor-pointer hover:bg-subtle",
          row.changed && "bg-adjusted-bg",
        )}
        onClick={toggle}
      >
        <AssignSetCheckbox
          ids={[row.id]}
          state={row.checked ? "all" : "none"}
          onAssign={onAssign}
          onClick={stopPropagation}
          disabledReason={disabledReason}
          ariaLabel={`Assign ${row.storeName} to ${targetLabel}`}
        />
        <span className="paragraph-xs flex min-w-40 flex-1 items-center gap-1.5 truncate font-medium">
          <span className="truncate">{row.storeName}</span>
          {showDelta && row.changed && (
            <Badge size="xs" variant={row.checked ? "green" : "red"}>
              {row.checked ? "Added" : "Removed"}
            </Badge>
          )}
        </span>
        {columns.map((column) => (
          <span
            key={column.field}
            className={cn("paragraph-xs shrink-0 truncate text-fg-subtle", column.className)}
          >
            {column.render ? column.render(row) : cellText(row[column.field])}
          </span>
        ))}
      </div>
    )
  }

  return (
    <div
      className={cn(
        "flex flex-col overflow-hidden rounded-lg border border-base",
        fill && "min-h-0 flex-1",
      )}
    >
      <div className={cn("overflow-auto", bounded && "max-h-121", fill && "min-h-0 flex-1")}>
        <div className="min-w-max">
          <div className="label-sm sticky top-0 z-10 flex items-center gap-2.5 border-b border-base bg-subtle px-3 py-1.5 text-fg-muted">
            <AssignSetCheckbox
              ids={visible.map((row) => row.id)}
              state={assignStateOf(visible)}
              onAssign={onAssign}
              disabledReason={disabledReason}
              ariaLabel={`Assign all shown stores to ${targetLabel}`}
            />
            <span className="min-w-40 flex-1">Store</span>
            {columns.map((column) => (
              <span key={column.field} className={cn("shrink-0 truncate", column.className)}>
                {column.name}
              </span>
            ))}
          </div>
          {groups
            ? groups.map((group) => {
                const open = view.narrowed || view.openGroups.has(group.key)
                return (
                  <div key={group.key}>
                    <div
                      role={view.narrowed ? undefined : "button"}
                      tabIndex={view.narrowed ? undefined : 0}
                      aria-expanded={view.narrowed ? undefined : open}
                      className={cn(
                        "flex items-center gap-2.5 border-b border-base px-3 py-1.5",
                        !view.narrowed && "cursor-pointer hover:bg-subtle",
                      )}
                      onClick={view.narrowed ? undefined : () => view.toggleGroup(group.key)}
                      onKeyDown={
                        view.narrowed
                          ? undefined
                          : (event) => {
                              if (event.target !== event.currentTarget) return
                              if (event.key !== "Enter" && event.key !== " ") return
                              event.preventDefault()
                              view.toggleGroup(group.key)
                            }
                      }
                    >
                      <AssignSetCheckbox
                        ids={group.ids}
                        state={group.state}
                        onAssign={onAssign}
                        onClick={stopPropagation}
                        disabledReason={disabledReason}
                        ariaLabel={`Assign every ${group.key || "unset"} store to ${targetLabel}`}
                      />
                      <CaretRight
                        size={12}
                        className={cn(
                          "shrink-0 text-fg-muted transition-transform",
                          open && "rotate-90",
                        )}
                      />
                      <span className="label-sm text-fg-base">{group.key || "—"}</span>
                      <span className="paragraph-xs text-fg-muted tabular-nums">
                        {group.assigned} of {group.rows.length} assigned
                      </span>
                    </div>
                    {open && group.rows.map((row) => storeRow(row, true))}
                  </div>
                )
              })
            : visible.map((row) => storeRow(row, false))}
          {visible.length === 0 && (
            <div className="paragraph-xs px-3 py-4 text-fg-muted">
              {emptyText(loading, view.rowCount)}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

type StoreAssignmentTableProps<T extends StoreAssignmentRow> = StoreAssignmentOptions<T> & {
  note: ReactNode
  targetLabel: string
  loading?: boolean
}

/** Note, toolbar and rows in one block, for surfaces that don't put the toolbar in a header. */
export function StoreAssignmentTable<T extends StoreAssignmentRow>(
  props: StoreAssignmentTableProps<T>,
) {
  return (
    <FilterScopeProvider scopeKey={props.scopeKey}>
      <Table {...props} />
    </FilterScopeProvider>
  )
}

function Table<T extends StoreAssignmentRow>({
  note,
  targetLabel,
  loading,
  ...options
}: StoreAssignmentTableProps<T>) {
  const view = useStoreAssignment(options)
  return (
    <>
      <div className="mb-2 flex items-center gap-2">
        <div className="paragraph-xs flex min-w-0 flex-1 items-center gap-1.5 text-fg-muted">
          <Info className="size-3.5 shrink-0" />
          <span className="truncate">{note}</span>
        </div>
        <StoreAssignmentToolbar view={view} />
      </div>
      <StoreAssignmentRows view={view} targetLabel={targetLabel} loading={loading} />
    </>
  )
}
